import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Menu from '../../components/menu/Menu';
import NavBar from '../../components/navbar/NavBar';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import ReactLoading from 'react-loading';
import {
  FaDownload,
  FaChartLine,
  FaCheckCircle,
  FaEdit,
  FaTimesCircle,
  FaClock,
  FaExclamationTriangle,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaHourglass
} from 'react-icons/fa';
import {
  fetchAIPerformanceSummary,
  fetchAIPerformanceTrends,
  fetchAIPerformanceByGrouper,
  fetchAIPerformanceCompare
} from '../../services/api/fetchAIPerformance';
import './AIAnalyticsReport.css';

const AIAnalyticsReport = () => {
  const { clientId } = useParams();
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');

  // Data states
  const [summaryData, setSummaryData] = useState(null);
  const [trendsData, setTrendsData] = useState([]);
  const [grouperData, setGrouperData] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);

  // Error and success states
  const [errorMessage, setErrorMessage] = useState('');
  const [partialErrors, setPartialErrors] = useState({
    summary: null,
    trends: null,
    groupers: null,
    comparison: null
  });

  // Set default date range (last 30 days)
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setDateRange([start, end]);
  }, []);

  // Fetch data when date range changes
  useEffect(() => {
    if (startDate && endDate) {
      fetchReportData();
    }
  }, [startDate, endDate, clientId]);

  const getErrorMessage = (error, endpoint) => {
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;

      if (status === 400) {
        return data?.detail || data?.error || 'Parâmetros inválidos';
      } else if (status === 401 || status === 403) {
        return 'Sem permissão para acessar este recurso';
      } else if (status === 404) {
        return 'Dados não encontrados para o período selecionado';
      } else if (status === 500) {
        return data?.error || 'Erro interno do servidor';
      }
    }
    return `Erro ao carregar ${endpoint}`;
  };

  const fetchWithErrorHandling = async (fetchFn, params, endpoint) => {
    try {
      const response = await fetchFn(params);
      // Check if response contains an error field
      if (response && response.error) {
        return { data: null, error: response.error };
      }
      return { data: response, error: null };
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      return { data: null, error: getErrorMessage(error, endpoint) };
    }
  };

  const fetchReportData = async () => {
    setIsLoading(true);
    setErrorMessage('');
    setPartialErrors({ summary: null, trends: null, groupers: null, comparison: null });

    const params = {
      start_date: formatDate(startDate),
      end_date: formatDate(endDate),
      client_id: clientId
    };

    // Calculate comparison period (previous period of same length)
    const periodLength = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const prevEndDate = new Date(startDate);
    prevEndDate.setDate(prevEndDate.getDate() - 1);
    const prevStartDate = new Date(prevEndDate);
    prevStartDate.setDate(prevStartDate.getDate() - periodLength);

    const compareParams = {
      period1_start: formatDate(prevStartDate),
      period1_end: formatDate(prevEndDate),
      period2_start: formatDate(startDate),
      period2_end: formatDate(endDate),
      client_id: clientId
    };

    // Fetch all data in parallel with individual error handling
    const [summaryResult, trendsResult, groupersResult, comparisonResult] = await Promise.all([
      fetchWithErrorHandling(fetchAIPerformanceSummary, params, 'resumo'),
      fetchWithErrorHandling(fetchAIPerformanceTrends, { ...params, period_type: 'week', limit: 12 }, 'tendências'),
      fetchWithErrorHandling(fetchAIPerformanceByGrouper, params, 'dados por grouper'),
      fetchWithErrorHandling(fetchAIPerformanceCompare, compareParams, 'comparação')
    ]);

    // Process results
    const errors = {};
    let hasAnyData = false;

    if (summaryResult.error) {
      errors.summary = summaryResult.error;
      setSummaryData(null);
    } else {
      setSummaryData(summaryResult.data);
      hasAnyData = true;
    }

    if (trendsResult.error) {
      errors.trends = trendsResult.error;
      setTrendsData([]);
    } else {
      const trends = trendsResult.data;
      setTrendsData(Array.isArray(trends) ? trends : (trends?.results || []));
      hasAnyData = true;
    }

    if (groupersResult.error) {
      errors.groupers = groupersResult.error;
      setGrouperData([]);
    } else {
      const groupers = groupersResult.data;
      setGrouperData(Array.isArray(groupers) ? groupers : (groupers?.results || []));
      hasAnyData = true;
    }

    if (comparisonResult.error) {
      errors.comparison = comparisonResult.error;
      setComparisonData(null);
    } else {
      setComparisonData(comparisonResult.data);
    }

    setPartialErrors(errors);

    // Show general error only if all endpoints failed
    if (!hasAnyData && Object.keys(errors).length > 0) {
      setErrorMessage('Não foi possível carregar os dados do relatório. Verifique se existem dados para o período selecionado.');
    }

    setIsLoading(false);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatPercentage = (value) => {
    return value != null ? `${Number(value).toFixed(1)}%` : '0.0%';
  };

  const formatNumber = (value) => {
    return value != null ? Number(value).toLocaleString('pt-BR') : '0';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <FaArrowUp className="trend-icon trend-up" />;
    if (trend === 'declining') return <FaArrowDown className="trend-icon trend-down" />;
    return <FaMinus className="trend-icon trend-stable" />;
  };

  const getTrendLabel = (trend) => {
    if (trend === 'improving') return 'Melhorando';
    if (trend === 'declining') return 'Declinando';
    return 'Estável';
  };

  // Precision Trend Chart
  const getPrecisionTrendChartOptions = () => {
    if (!trendsData || trendsData.length === 0) return null;

    const sortedData = [...trendsData].sort((a, b) =>
      new Date(a.start_date) - new Date(b.start_date)
    );

    const labels = sortedData.map(item => {
      const date = new Date(item.start_date);
      return `${date.getDate()}/${date.getMonth() + 1}`;
    });
    const accuracyRates = sortedData.map(item => item.avg_accuracy_rate || 0);
    const revisionRates = sortedData.map(item => item.avg_revision_rate || 0);

    return {
      chart: {
        type: 'area',
        backgroundColor: '#ffffff',
        height: 350
      },
      title: {
        text: 'Tendência de Precisão ao Longo do Tempo',
        style: { fontSize: '16px', fontWeight: '600', color: '#1a1a2e' }
      },
      xAxis: {
        categories: labels,
        title: { text: 'Período' }
      },
      yAxis: {
        title: { text: 'Percentual (%)' },
        min: 0,
        max: 100
      },
      tooltip: {
        shared: true,
        valueSuffix: '%'
      },
      plotOptions: {
        area: {
          fillOpacity: 0.3,
          marker: { enabled: true, radius: 4 }
        }
      },
      series: [
        {
          name: 'Taxa de Precisão',
          data: accuracyRates,
          color: '#10b981'
        },
        {
          name: 'Taxa de Revisão',
          data: revisionRates,
          color: '#f59e0b'
        }
      ],
      credits: { enabled: false }
    };
  };

  // Value Distribution Chart
  const getValueDistributionChartOptions = () => {
    if (!summaryData?.value_distribution) return null;

    const distribution = summaryData.value_distribution;
    const data = Object.entries(distribution).map(([name, value]) => ({
      name,
      y: value,
      color: getValueColor(name)
    }));

    if (data.length === 0) return null;

    return {
      chart: {
        type: 'pie',
        backgroundColor: '#ffffff',
        height: 350
      },
      title: {
        text: 'Distribuição de Valores Avaliados',
        style: { fontSize: '16px', fontWeight: '600', color: '#1a1a2e' }
      },
      tooltip: {
        pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)'
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.percentage:.1f}%'
          }
        }
      },
      series: [{
        name: 'Avaliações',
        colorByPoint: true,
        data: data
      }],
      credits: { enabled: false }
    };
  };

  const getValueColor = (value) => {
    const colors = {
      'Positivo': '#10b981',
      'Negativo': '#ef4444',
      'Neutro': '#6b7280',
      'Equilibrado': '#3b82f6'
    };
    return colors[value] || '#8b5cf6';
  };

  // Grouper Performance Chart
  const getGrouperChartOptions = () => {
    if (!grouperData || grouperData.length === 0) return null;

    const sortedData = [...grouperData]
      .sort((a, b) => (b.avg_accuracy || 0) - (a.avg_accuracy || 0))
      .slice(0, 10);

    return {
      chart: {
        type: 'bar',
        backgroundColor: '#ffffff',
        height: 400
      },
      title: {
        text: 'Performance por Tipo de Avaliação (Top 10)',
        style: { fontSize: '16px', fontWeight: '600', color: '#1a1a2e' }
      },
      xAxis: {
        categories: sortedData.map(item => item.grouper__name || 'N/A'),
        title: { text: null }
      },
      yAxis: {
        min: 0,
        max: 100,
        title: { text: 'Taxa de Precisão (%)' }
      },
      tooltip: {
        valueSuffix: '%'
      },
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
            format: '{y:.1f}%'
          }
        }
      },
      series: [{
        name: 'Precisão',
        data: sortedData.map(item => Number((item.avg_accuracy || 0).toFixed(1))),
        color: '#3b82f6'
      }],
      credits: { enabled: false }
    };
  };

  const handleExportPDF = () => {
    window.print();
  };

  const precisionTrendOptions = getPrecisionTrendChartOptions();
  const valueDistributionOptions = getValueDistributionChartOptions();
  const grouperChartOptions = getGrouperChartOptions();

  return (
    <div className="ai-report-container">
      <Menu onToggleMenu={setIsMenuExpanded} />
      <div className={`ai-report-content ${isMenuExpanded ? 'menu-expanded' : ''}`}>
        <NavBar />

        <div className="ai-report-main">
          {/* Header */}
          <div className="ai-report-header">
            <div className="header-info">
              <h1 className="ai-report-title">Relatório de Performance da IA</h1>
              <p className="ai-report-subtitle">
                Análise detalhada das métricas de avaliação automática
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="ai-report-filters">
            <div className="filter-group">
              <label className="filter-label">Período de Análise</label>
              <DatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => setDateRange(update)}
                dateFormat="dd/MM/yyyy"
                className="date-picker-input"
                placeholderText="Selecione o período"
              />
            </div>

            {/* Tabs */}
            <div className="report-tabs">
              <button
                className={`tab-btn ${activeTab === 'summary' ? 'active' : ''}`}
                onClick={() => setActiveTab('summary')}
              >
                Resumo
              </button>
              <button
                className={`tab-btn ${activeTab === 'trends' ? 'active' : ''}`}
                onClick={() => setActiveTab('trends')}
              >
                Tendências
              </button>
              <button
                className={`tab-btn ${activeTab === 'groupers' ? 'active' : ''}`}
                onClick={() => setActiveTab('groupers')}
              >
                Por Avaliação
              </button>
            </div>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="alert alert-error">
              <FaExclamationTriangle /> {errorMessage}
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="loading-container">
              <ReactLoading type="spin" color="#3b82f6" height={50} width={50} />
              <p className="loading-text">Carregando relatório...</p>
            </div>
          )}

          {/* Content */}
          {!isLoading && (summaryData || trendsData.length > 0 || grouperData.length > 0) && (
            <div className="ai-report-body">
              {/* Summary Tab */}
              {activeTab === 'summary' && (
                <>
                  {/* Partial Error Warning */}
                  {partialErrors.summary && (
                    <div className="alert alert-warning">
                      <FaExclamationTriangle /> {partialErrors.summary}
                    </div>
                  )}

                  {summaryData ? (
                    <>
                      {/* Metrics Cards */}
                      <div className="metrics-cards">
                        <div className="metric-card card-accuracy">
                          <div className="card-icon"><FaCheckCircle /></div>
                          <div className="card-content">
                            <span className="card-value">{formatPercentage(summaryData.accuracy_rate)}</span>
                            <span className="card-label">Taxa de Precisão</span>
                            <span className="card-detail">
                              {formatNumber(summaryData.total_approved_unchanged)} aprovações sem alteração
                            </span>
                          </div>
                          {comparisonData && (
                            <div className="card-trend">
                              {getTrendIcon(comparisonData.trend)}
                              <span className={`trend-value ${comparisonData.trend}`}>
                                {comparisonData.accuracy_change > 0 ? '+' : ''}{comparisonData.accuracy_change?.toFixed(1)}%
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="metric-card card-revision">
                          <div className="card-icon"><FaEdit /></div>
                          <div className="card-content">
                            <span className="card-value">{formatPercentage(summaryData.revision_rate)}</span>
                            <span className="card-label">Taxa de Revisão</span>
                            <span className="card-detail">
                              {formatNumber(summaryData.total_revised)} revisões realizadas
                            </span>
                          </div>
                        </div>

                        <div className="metric-card card-time">
                          <div className="card-icon"><FaClock /></div>
                          <div className="card-content">
                            <span className="card-value"> 
                              {summaryData.avg_approval_time_hours
                                ? `${summaryData.avg_approval_time_hours.toFixed(1)}h`
                                : 'N/A'}
                            </span>
                            <span className="card-label">Tempo Médio de Aprovação</span>
                            <span className="card-detail">
                              {formatNumber(summaryData.total_ai_evaluations)} avaliações totais
                            </span>
                          </div>
                        </div>

                        <div className="metric-card card-pending">
                          <div className="card-icon"><FaHourglass /></div>
                          <div className="card-content">
                            <span className="card-value">{formatNumber(summaryData.total_pending)}</span>
                            <span className="card-label">Avaliações Pendentes</span>
                            <span className="card-detail">
                              Aguardando revisão humana
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Summary Charts */}
                      <div className="charts-row">
                        {valueDistributionOptions && (
                          <div className="chart-card">
                            <HighchartsReact
                              highcharts={Highcharts}
                              options={valueDistributionOptions}
                            />
                          </div>
                        )}

                        {/* Revision Patterns */}
                        {summaryData.revision_patterns && Object.keys(summaryData.revision_patterns).length > 0 && (
                          <div className="chart-card patterns-card">
                            <h3 className="card-title">Padrões de Revisão Mais Comuns</h3>
                            <div className="patterns-list">
                              {Object.entries(summaryData.revision_patterns)
                                .sort((a, b) => b[1] - a[1])
                                .slice(0, 5)
                                .map(([pattern, count], index) => (
                                  <div key={index} className="pattern-item">
                                    <span className="pattern-text">{pattern}</span>
                                    <span className="pattern-count">{formatNumber(count)}</span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Top/Bottom Groupers */}
                      <div className="grouper-highlights">
                        {summaryData.top_groupers_by_accuracy?.length > 0 && (
                          <div className="highlight-card top-performers">
                            <h3 className="highlight-title">
                              <FaArrowUp className="highlight-icon success" /> Melhores Performances
                            </h3>
                            <ul className="highlight-list">
                              {summaryData.top_groupers_by_accuracy.slice(0, 5).map((item, index) => (
                                <li key={index} className="highlight-item">
                                  <span className="item-name">{item.grouper__name || 'N/A'}</span>
                                  <span className="item-value success">
                                    {formatPercentage(item.avg_accuracy)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {summaryData.groupers_needing_attention?.length > 0 && (
                          <div className="highlight-card needs-attention">
                            <h3 className="highlight-title">
                              <FaExclamationTriangle className="highlight-icon warning" /> Precisam de Atenção
                            </h3>
                            <ul className="highlight-list">
                              {summaryData.groupers_needing_attention.slice(0, 5).map((item, index) => (
                                <li key={index} className="highlight-item">
                                  <span className="item-name">{item.grouper__name || 'N/A'}</span>
                                  <span className="item-value warning">
                                    {formatPercentage(item.avg_accuracy)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="empty-state">
                      <FaChartLine size={48} color="#ccc" />
                      <h3>Dados de resumo indisponíveis</h3>
                      <p>Não foi possível carregar o resumo para este período.</p>
                    </div>
                  )}
                </>
              )}

              {/* Trends Tab */}
              {activeTab === 'trends' && (
                <div className="trends-section">
                  {/* Partial Error Warning */}
                  {partialErrors.trends && (
                    <div className="alert alert-warning">
                      <FaExclamationTriangle /> {partialErrors.trends}
                    </div>
                  )}

                  {precisionTrendOptions ? (
                    <div className="chart-card full-width">
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={precisionTrendOptions}
                      />
                    </div>
                  ) : (
                    <div className="empty-state">
                      <FaChartLine size={48} color="#ccc" />
                      <p>Sem dados de tendência para o período selecionado.</p>
                    </div>
                  )}

                  {/* Trend Cards */}
                  {trendsData.length > 0 && (
                    <div className="trend-cards">
                      {trendsData.slice(0, 6).map((trend, index) => (
                        <div key={index} className="trend-card">
                          <div className="trend-period">
                            {new Date(trend.start_date).toLocaleDateString('pt-BR')} -
                            {new Date(trend.end_date).toLocaleDateString('pt-BR')}
                          </div>
                          <div className="trend-metrics">
                            <div className="trend-metric">
                              <span className="metric-label">Precisão</span>
                              <span className="metric-value">{formatPercentage(trend.avg_accuracy_rate)}</span>
                            </div>
                            <div className="trend-metric">
                              <span className="metric-label">Avaliações</span>
                              <span className="metric-value">{formatNumber(trend.total_evaluations)}</span>
                            </div>
                          </div>
                          <div className="trend-status">
                            {getTrendIcon(trend.accuracy_trend)}
                            <span className={`trend-label ${trend.accuracy_trend}`}>
                              {getTrendLabel(trend.accuracy_trend)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Groupers Tab */}
              {activeTab === 'groupers' && (
                <div className="groupers-section">
                  {/* Partial Error Warning */}
                  {partialErrors.groupers && (
                    <div className="alert alert-warning">
                      <FaExclamationTriangle /> {partialErrors.groupers}
                    </div>
                  )}

                  {grouperChartOptions && (
                    <div className="chart-card full-width">
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={grouperChartOptions}
                      />
                    </div>
                  )}

                  {/* Groupers Table */}
                  {grouperData.length > 0 && (
                    <div className="table-card">
                      <h3 className="table-title">Detalhamento por Tipo de Avaliação</h3>
                      <div className="table-wrapper">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Tipo de Avaliação</th>
                              <th>Total</th>
                              <th>Aprovadas</th>
                              <th>Revisadas</th>
                              <th>Taxa de Precisão</th>
                              <th>Taxa de Revisão</th>
                            </tr>
                          </thead>
                          <tbody>
                            {grouperData.map((item, index) => (
                              <tr key={index}>
                                <td className="grouper-name">{item.grouper__name || 'N/A'}</td>
                                <td>{formatNumber(item.total_evaluations)}</td>
                                <td className="success">{formatNumber(item.total_approved)}</td>
                                <td className="warning">{formatNumber(item.total_revised)}</td>
                                <td className={`accuracy ${(item.avg_accuracy || 0) >= 80 ? 'high' : (item.avg_accuracy || 0) >= 60 ? 'medium' : 'low'}`}>
                                  {formatPercentage(item.avg_accuracy)}
                                </td>
                                <td>{formatPercentage(item.avg_revision_rate)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !summaryData && !errorMessage && (
            <div className="empty-state">
              <FaChartLine size={64} color="#ccc" />
              <h3>Nenhum dado disponível</h3>
              <p>Selecione um período para visualizar o relatório de performance da IA.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIAnalyticsReport;